import { createContext } from 'react'
import type {
  Customer,
  CustomerActivityStatus,
  CustomerChannel,
  CustomerListParams,
  CustomerListResponse,
  CustomerSegment,
} from '../../../types/customer'
import type { CustomerAdapter } from '../adapters/types'

// Snapshot of derived stats over the current page of customers. Computed
// client-side in `useCustomerList`. Modelled on `UserContext.stats`.
export interface CustomerStats {
  total: number
  active: number
  new: number
  lapsed: number
  inactive: number
}

export type CustomerSegmentFilter = CustomerSegment | 'all'
export type CustomerActivityFilter = CustomerActivityStatus | 'all'
export type CustomerChannelFilter = CustomerChannel | 'all'
export type CustomerSpendFilter =
  | 'lt100'
  | '100_500'
  | '500_1k'
  | 'gt1k'
  | 'all'

export interface CustomerContextValue {
  // Resolved per-store adapter — the only thing downstream hooks should
  // talk to. Provider picks it via `resolveCustomerAdapter` at mount.
  adapter: CustomerAdapter
  storeId: string

  // Server data
  pageData: CustomerListResponse | undefined
  customers: Customer[]
  isLoading: boolean
  isFetching: boolean
  isError: boolean
  error: unknown

  // Derived stats (computed across current page)
  stats: CustomerStats

  // Search (debounced → also sent to server)
  keyword: string
  setKeyword: (value: string) => void

  // Filters (server-side)
  segmentFilter: CustomerSegmentFilter
  setSegmentFilter: (value: CustomerSegmentFilter) => void
  activityFilter: CustomerActivityFilter
  setActivityFilter: (value: CustomerActivityFilter) => void
  channelFilter: CustomerChannelFilter
  setChannelFilter: (value: CustomerChannelFilter) => void
  spendFilter: CustomerSpendFilter
  setSpendFilter: (value: CustomerSpendFilter) => void
  // Location filter — value is a `WHLocation.id` (POS location id), 'all'
  // clears it. Options are sourced from `usePantryWarehouses()`, mirroring
  // the Order module's Locations select.
  locationFilter: string
  setLocationFilter: (value: string) => void

  // Pagination
  listParams: CustomerListParams
  currentPage: number
  totalPages: number
  totalItems: number
  canGoBack: boolean
  canGoNext: boolean
  showingStart: number
  showingEnd: number
  goToPrevPage: () => void
  goToNextPage: () => void
  goToPage: (page: number) => void
  setLimit: (limit: number) => void

  // Sorting (server-side)
  sortBy: string | undefined
  sort: 'asc' | 'desc' | undefined
  setSort: (sortBy: string | undefined, sort?: 'asc' | 'desc') => void

  // Row-click navigation. Populated by the route wrapper (which knows about
  // React Router); defaults to a no-op so the provider stays usable outside
  // a router. When provided, the table renders rows with a pointer cursor.
  onSelectCustomer?: (customerId: string) => void

  // ── Export-CSV plumbing ─────────────────────────────────────────────────
  // The CSV export flow matches the legacy main-app behaviour: small filtered
  // sets stream the CSV back inline (we download immediately), large sets
  // queue an async job and the user is notified + emailed. Pantry-features
  // can't reach the host app's dialog/toast primitives, so the consumer
  // wires these via the route wrapper. All three default to no-op.
  //
  // `timeZone` is the user's UTC-offset (e.g. -5 for EST), computed by the
  // host wrapper from `user.user_settings.tz_setting` and forwarded here.
  timeZone?: number
  onExportQueued?: (exportId: string) => void
  onExportReady?: () => void
  onExportError?: (error: unknown) => void
}

export const CustomerContext = createContext<CustomerContextValue | undefined>(
  undefined,
)
